/**
 * Levar o erro que a API recusou para o campo que o causou.
 *
 * Papel: aqui fica `aplicarErroDaApi`, que distribui as mensagens de
 * `erro.campos` pelos campos do react-hook-form e devolve o que sobrou, para a
 * tela mostrar acima dos botões.
 *
 * Como usar: todo formulário chama esta função no `catch` do envio e mostra o
 * retorno quando ele não for `null`. O que a função **não** faz é decidir onde
 * a mensagem aparece — sem isso, uma gravação recusada falharia em silêncio, e
 * é esse o defeito que ela existe para evitar.
 *
 * O mapa de campos é do formulário, e não daqui: só ele sabe traduzir o caminho
 * que a API devolveu — `nome`, ou `telefones.0.numero` — num caminho que o
 * react-hook-form reconheça. Um caminho desconhecido marcaria um campo que não
 * existe, e a mensagem sumiria da tela sem nada acusar; por isso o que não é
 * reconhecido volta como mensagem geral em vez de ser descartado.
 */
import { type FieldPath, type FieldValues, type UseFormReturn } from 'react-hook-form';

import { ErroDaApi } from '@/shared/api';

/**
 * Traduz um caminho vindo da API num caminho do formulário.
 *
 * @param caminho - A chave como a Function a montou.
 * @returns O caminho do formulário, ou `null` quando não corresponde a campo
 *   algum.
 */
export type TradutorDeCampo<T extends FieldValues> = (caminho: string) => FieldPath<T> | null;

/**
 * Marca nos campos o que a API recusou e devolve o que não coube em nenhum.
 *
 * Os três parâmetros de tipo acompanham os de `useForm`: o formulário guarda
 * uma forma na entrada e devolve outra na saída — string vazia aqui, `null`
 * ali —, e prendê-los a um só faria a função recusar justamente os formulários
 * que usam `zodResolver` com transformação.
 *
 * @param erro - O valor capturado no `catch` do envio.
 * @param form - O formulário cujos campos devem receber as mensagens.
 * @param traduzir - Converte o caminho da API no caminho do formulário.
 * @returns A mensagem a mostrar acima dos botões, ou `null` quando tudo coube
 *   em algum campo.
 */
export function aplicarErroDaApi<
  TEntrada extends FieldValues,
  TContexto,
  TSaida extends FieldValues,
>(
  erro: unknown,
  form: UseFormReturn<TEntrada, TContexto, TSaida>,
  traduzir: TradutorDeCampo<TEntrada>,
): string | null {
  if (!(erro instanceof ErroDaApi)) {
    return 'A gravação falhou antes de chegar ao servidor.';
  }

  let marcouAlgumCampo = false;
  const semCampo: string[] = [];

  for (const [caminho, mensagens] of Object.entries(erro.campos ?? {})) {
    const mensagem = mensagens[0];
    if (mensagem === undefined) continue;

    const campo = traduzir(caminho);
    if (campo === null) {
      semCampo.push(mensagem);
      continue;
    }

    form.setError(campo, { message: mensagem });
    marcouAlgumCampo = true;
  }

  if (semCampo.length > 0) return semCampo.join(' ');
  return marcouAlgumCampo ? null : erro.message;
}

/**
 * Monta um tradutor a partir de um mapa simples de nomes de campo.
 *
 * Serve ao formulário cujos campos são todos de primeiro nível — a maioria
 * deles. Quem tem campo aninhado, como a lista de telefones do Mailing, escreve
 * o próprio tradutor.
 *
 * @param mapa - Do caminho que a API devolve para o caminho do formulário.
 * @returns O tradutor, que devolve `null` para o que não está no mapa.
 */
export function tradutorPorMapa<T extends FieldValues>(
  mapa: Record<string, FieldPath<T> | undefined>,
): TradutorDeCampo<T> {
  return (caminho) => mapa[caminho] ?? null;
}
